/**
 * Live "what the strategist is doing" stream, shown under the Run button while
 * the strategist runs. Reads the job's progress events (strategist_thinking =
 * the model's real per-turn reasoning; strategist_activity = the tool step it's
 * taking) and renders them as a live stream — the same peek the dialogue panel
 * gives, but for the initial run.
 */

import React, { useEffect, useRef } from 'react';

function ThinkingDots() {
  return (
    <span className="inline-flex gap-1 items-center">
      {[0, 150, 300].map((d) => (
        <span key={d} className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />
      ))}
    </span>
  );
}

export function StrategistLiveActivity({ progress, running }: { progress: any[]; running: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const stream = (progress || [])
    .filter((p) => p?.event === 'strategist_thinking' || p?.event === 'strategist_activity')
    .map((p) => ({
      kind: (p.event === 'strategist_thinking' ? 'thinking' : 'activity') as 'thinking' | 'activity',
      text: (p.text || p.message || '').trim(),
    }))
    .filter((x) => x.text);

  // Auto-scroll the box (not the page) as new events land.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [stream.length, running]);

  if (stream.length === 0 && !running) return null;

  return (
    <div className="border border-purple-200 bg-purple-50/60 rounded-lg p-3">
      <div className="text-xs font-semibold text-purple-800 mb-2">What the strategist is doing</div>
      <div ref={scrollRef} className="space-y-1.5 max-h-72 overflow-y-auto">
        {stream.map((t, i) =>
          t.kind === 'thinking' ? (
            <div key={i} className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed border-l-2 border-purple-200 pl-2">
              {t.text}
            </div>
          ) : (
            <div key={i} className="text-[11px] text-purple-500 flex items-start gap-1">
              <span className="mt-px">→</span>
              <span>{t.text}</span>
            </div>
          )
        )}
        {running && (
          <div className="flex items-center gap-2 text-xs text-purple-600">
            <ThinkingDots />
            <span className="italic">thinking…</span>
          </div>
        )}
      </div>
    </div>
  );
}
